import type { ComfortSettings } from '@galley/shared'
import { useEffect, useRef } from 'react'
import { useUpdateComfort } from '../api/hooks'
import { useSession } from '../session/SessionProvider'
import { useComfort } from './ComfortProvider'

function sameComfort(a: ComfortSettings, b: ComfortSettings): boolean {
  return (
    a.calm === b.calm &&
    a.night === b.night &&
    a.readingStyle === b.readingStyle &&
    a.contrast === b.contrast
  )
}

/**
 * Students move between shared Chromebooks, so their Comfort settings live on the server
 * (PATCH /api/s/comfort). On sign-in we adopt the saved copy; later changes are saved back,
 * debounced. Teachers and visitors keep settings on the device only. Renders nothing.
 */
export function StudentComfortSync() {
  const { student } = useSession()
  const { prefs, adoptPrefs } = useComfort()
  const { mutate: saveComfort } = useUpdateComfort()
  const adoptedFor = useRef<string | null>(null)

  useEffect(() => {
    if (!student) {
      adoptedFor.current = null
      return
    }
    if (adoptedFor.current === student.memberId) return
    adoptedFor.current = student.memberId
    adoptPrefs(student.comfort)
  }, [student, adoptPrefs])

  useEffect(() => {
    if (!student || adoptedFor.current !== student.memberId) return
    if (sameComfort(prefs, student.comfort)) return
    const timer = setTimeout(() => saveComfort(prefs), 600)
    return () => clearTimeout(timer)
  }, [prefs, student, saveComfort])

  return null
}
